import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useStore } from '@/store/useStore';
import { getAvailableThemes } from '@/config/themes';
import { Palette, Sparkles, Shield, ArrowRight, Mail, Trash2, CheckCircle, User, Lock, Building2 } from 'lucide-react';
import { CompanyProfileSettings } from '@/components/proposals/CompanyProfileSettings';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/services/api';
import { toast } from 'sonner';

interface ContactMessage {
  id: number;
  email: string;
  message: string;
  created_at: string;
  read: boolean;
  ip_address?: string;
  user_agent?: string;
}

export default function Settings() {
  const { t } = useTranslation();
  const { selectedTheme, setSelectedTheme } = useStore();
  const availableThemes = getAvailableThemes();
  const navigate = useNavigate();
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const [userEmail, setUserEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/auth/me');
        setIsSuperuser(response.data.is_superuser || false);
        setUserEmail(response.data.email || '');
        setFullName(response.data.full_name || '');
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    };
    fetchProfile();
  }, []);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await api.put('/auth/profile', { full_name: fullName });
      setFullName(response.data.full_name || '');
      toast.success(t('settings.profileUpdated'));
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.passwordsDontMatch'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('settings.passwordTooShort'));
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success(t('settings.passwordUpdated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchContactMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await api.get('/contact/messages', {
        params: { unread_only: showUnreadOnly }
      });
      setContactMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch contact messages:', error);
      toast.error('Failed to load contact messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const markAsRead = async (messageId: number) => {
    try {
      await api.patch(`/contact/messages/${messageId}/mark-read`);
      toast.success(t('settings.messageMarkedRead'));
      fetchContactMessages();
    } catch (error) {
      console.error('Failed to mark message as read:', error);
      toast.error('Failed to mark message as read');
    }
  };

  const deleteMessage = async (messageId: number) => {
    try {
      await api.delete(`/contact/messages/${messageId}`);
      toast.success(t('settings.messageDeleted'));
      fetchContactMessages();
    } catch (error) {
      console.error('Failed to delete message:', error);
      toast.error('Failed to delete message');
    }
  };

  useEffect(() => {
    if (isSuperuser) {
      fetchContactMessages();
    }
  }, [isSuperuser, showUnreadOnly]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
        <p className="text-muted-foreground">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Appearance Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="h-5 w-5" />
            {t('settings.appearance')}
          </CardTitle>
          <CardDescription>
            {t('settings.appearanceDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="theme-select">{t('settings.theme')}</Label>
            <Select value={selectedTheme} onValueChange={setSelectedTheme}>
              <SelectTrigger id="theme-select" className="w-full max-w-md">
                <SelectValue placeholder={t('settings.selectTheme')} />
              </SelectTrigger>
              <SelectContent>
                {availableThemes.map((theme) => (
                  <SelectItem key={theme.id} value={theme.id}>
                    <div className="flex items-center gap-2">
                      <span>{theme.name}</span>
                      {theme.id === selectedTheme && (
                        <Sparkles className="h-3 w-3 text-primary" />
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              {availableThemes.find(th => th.id === selectedTheme)?.description || t('settings.chooseTheme')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('settings.profile')}
          </CardTitle>
          <CardDescription>
            {t('settings.profileDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('settings.email')}</Label>
            <Input
              id="email"
              type="email"
              value={userEmail}
              disabled
              className="max-w-md bg-muted"
            />
            <p className="text-sm text-muted-foreground">
              {t('settings.emailNoChange')}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="full-name">{t('settings.fullName')}</Label>
            <div className="flex gap-2 max-w-md">
              <Input
                id="full-name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('settings.fullNamePlaceholder')}
              />
              <Button onClick={handleSaveProfile} disabled={savingProfile}>
                {savingProfile ? t('common.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {t('settings.changePassword')}
          </CardTitle>
          <CardDescription>
            {t('settings.changePasswordDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">{t('settings.currentPassword')}</Label>
            <Input
              id="current-password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder={t('settings.currentPasswordPlaceholder')}
              className="max-w-md"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">{t('settings.newPassword')}</Label>
            <Input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder={t('settings.newPasswordPlaceholder')}
              className="max-w-md"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t('settings.confirmPassword')}</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('settings.confirmPasswordPlaceholder')}
              className="max-w-md"
            />
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
          >
            {changingPassword ? t('settings.updating') : t('settings.changePassword')}
          </Button>
        </CardContent>
      </Card>

      {/* Company Profile for Service Proposals */}
      <div className="border-t pt-6 mt-6">
        <div className="mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('settings.serviceProposals')}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('settings.serviceProposalsDesc')}
          </p>
        </div>
        <CompanyProfileSettings />
      </div>

      {/* Admin Section - Only visible to superusers */}
      {isSuperuser && (
        <>
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                {t('settings.adminDashboard')}
              </CardTitle>
              <CardDescription>
                {t('settings.adminDashboardDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2"
              >
                {t('settings.openAdmin')}
                <ArrowRight className="h-4 w-4" />
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                {t('settings.adminDetails')}
              </p>
            </CardContent>
          </Card>

          {/* Contact Messages Section */}
          <Card className="border-primary/20">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  {t('settings.contactMessages')}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                  >
                    {showUnreadOnly ? t('settings.showAll') : t('settings.showUnreadOnly')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchContactMessages()}
                    disabled={loadingMessages}
                  >
                    {t('common.refresh')}
                  </Button>
                </div>
              </CardTitle>
              <CardDescription>
                {t('settings.contactMessagesDesc')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMessages ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t('settings.loadingMessages')}
                </div>
              ) : contactMessages.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {t('settings.noMessages')}
                </div>
              ) : (
                <div className="space-y-4">
                  {contactMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-lg border ${
                        msg.read ? 'bg-muted/20' : 'bg-primary/5 border-primary/20'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{msg.email}</span>
                            {!msg.read && (
                              <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">
                                {t('common.new')}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {new Date(msg.created_at).toLocaleString()}
                          </p>
                          <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          {msg.ip_address && (
                            <p className="text-xs text-muted-foreground">
                              IP: {msg.ip_address}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {!msg.read && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => markAsRead(msg.id)}
                              title="Mark as read"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (confirm(t('settings.deleteMessageConfirm'))) {
                                deleteMessage(msg.id);
                              }
                            }}
                            title="Delete message"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
